import { isMediaArtifactFile } from "@repo/shared";
import { FileWarning, MoreHorizontal, X } from "lucide-react";
import type { ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { formatFileSize } from "@/lib/formatters";
import { cn } from "@/lib/utils";

/**
 * The 4-character badge glyph, which is not the same thing as a TYPE column's extension
 * label (`@/lib/file-types`): it is truncated to fit a 36px square and falls back to the
 * MIME subtype so a pasted, extension-less file still shows something.
 */
function documentTypeLabel(fileName: string, mimeType: string): string {
  const fromName = fileName.includes(".") ? fileName.split(".").pop() : undefined;
  if (fromName) {
    return fromName.toUpperCase().slice(0, 4);
  }
  const fromMime = mimeType.split("/").pop();
  return (fromMime ?? "FILE").toUpperCase().slice(0, 4);
}

export function AttachmentDocumentChip({
  fileName,
  mimeType,
  fileSizeBytes,
  status = "unknown",
  progress = 0,
  error,
  compact,
  onOpen,
  onRemove,
  onRetry,
  onAddToProjectKnowledge,
  isAddingToProjectKnowledge,
  popover,
  isPopoverOpen,
  onPopoverOpenChange,
  className,
}: {
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  /**
   * `"unknown"` says nothing about the file beyond its type and size — the honest reading
   * where no pipeline state has been resolved for it. Every other value is a claim.
   *
   * `"waiting_for_you"` is not a slower `"processing"`: nothing is running, and nothing will
   * run until someone says how much of the file to read.
   */
  status?:
    | "uploading"
    | "attached"
    | "processing"
    | "waiting_for_you"
    | "ready"
    | "failed"
    | "unknown";
  progress?: number;
  error?: string;
  compact?: boolean;
  onOpen?: () => void;
  onRemove?: () => void;
  onRetry?: () => void | Promise<void>;
  onAddToProjectKnowledge?: () => void | Promise<void>;
  isAddingToProjectKnowledge?: boolean;
  /**
   * A panel the chip itself opens, anchored on its body.
   *
   * The chip stays presentational: it knows a panel exists and where it hangs, never what it
   * asks. Passing a `PopoverContent` here — today the Documents module's read-scope question —
   * is what lets the surface that owns the mutation compose it without this component learning
   * that feature's vocabulary. `onOpen` is ignored while a panel is given: one press cannot
   * both open a panel and open the file.
   */
  popover?: ReactNode;
  isPopoverOpen?: boolean;
  onPopoverOpenChange?: (open: boolean) => void;
  className?: string;
}) {
  const label = documentTypeLabel(fileName, mimeType);
  const isUploading = status === "uploading";
  const isProcessing = status === "processing";
  const isWaitingForYou = status === "waiting_for_you";
  const isFailed = status === "failed";
  // What is running on a recording is a transcription, and saying so is the difference between
  // a wait a person understands and a spinner they do not. The file the chip already names is
  // the whole of the question, so this stays a property of the chip rather than a prop.
  const processingText = isMediaArtifactFile(fileName, mimeType) ? "Transcribing…" : "Processing…";
  const statusText = isFailed
    ? (error ?? "Upload failed")
    : status === "attached"
      ? "Ready to send"
      : isProcessing
        ? processingText
        : isWaitingForYou
          ? "Waiting for you — choose what to read"
          : status === "ready"
            ? "Ready to read"
            : `${label} · ${formatFileSize(fileSizeBytes) ?? "Unknown size"}`;
  const canPress = Boolean(popover) || Boolean(onOpen);
  const body = (
    <button
      type="button"
      onClick={popover ? undefined : onOpen}
      disabled={!canPress}
      className={cn("flex min-w-0 items-center gap-2.5 text-left", canPress && "cursor-pointer")}
    >
      <div className="flex size-9 shrink-0 items-center justify-center border bg-background text-[9px] font-bold tracking-wide text-muted-foreground">
        {isFailed ? <FileWarning className="size-4 text-destructive" /> : label}
      </div>
      <div className={cn("flex min-w-0 flex-col gap-0.5", compact ? "w-[120px]" : "w-[150px]")}>
        <span className="truncate text-[13px] font-semibold leading-tight text-foreground">
          {fileName}
        </span>
        {isUploading ? (
          <div className="h-1 w-full overflow-hidden bg-border">
            <div
              className="h-full bg-primary transition-[width]"
              style={{ width: `${Math.max(4, progress)}%` }}
            />
          </div>
        ) : (
          <span className="truncate text-[11px] leading-tight text-muted-foreground">
            {statusText}
          </span>
        )}
      </div>
    </button>
  );
  return (
    <div
      data-testid="assistant-attachment-chip"
      data-attachment-status={status}
      className={cn(
        "group flex shrink-0 items-center gap-2.5 border bg-muted p-2 text-left",
        isFailed && "border-destructive",
        isWaitingForYou && "border-warning",
        className
      )}
    >
      {popover ? (
        <Popover open={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
          <PopoverTrigger render={body} />
          {popover}
        </Popover>
      ) : (
        body
      )}
      {isUploading || isProcessing ? (
        <span className="flex shrink-0 items-center gap-1 font-mono text-[11px] text-muted-foreground">
          <Spinner className="size-3" />
          {isUploading ? `${Math.round(progress)}%` : null}
        </span>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${fileName}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          className="flex size-5 shrink-0 items-center justify-center text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none group-hover:opacity-100"
        >
          <X className="size-3" />
        </button>
      ) : null}
      {isFailed && onRetry ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void onRetry();
          }}
          className="shrink-0 text-[11px] font-medium text-primary hover:underline"
        >
          Retry
        </button>
      ) : null}
      {onAddToProjectKnowledge ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <button
                type="button"
                aria-label={`More actions for ${fileName}`}
                className="flex size-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
              >
                {isAddingToProjectKnowledge ? (
                  <Spinner className="size-3" />
                ) : (
                  <MoreHorizontal className="size-3.5" />
                )}
              </button>
            }
          />
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={isAddingToProjectKnowledge}
              onClick={() => void onAddToProjectKnowledge()}
            >
              Add to project knowledge
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
